import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { RequestCard } from "../components/RequestCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { LocationSelect } from "../components/LocationSelect";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { SlidersHorizontal, Inbox } from "lucide-react";

export default function Feed() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [meta, setMeta] = useState({ sectors: [], locations: [] });
  const [data, setData] = useState({ requests: [], subscribed: false });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ sector: "all", location: "all", urgency: "all" });

  useEffect(() => { api.get("/meta").then((r) => setMeta(r.data)); }, []);

  const load = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    setLoading(true);
    try {
      const params = {};
      if (filters.sector !== "all") params.sector = filters.sector;
      if (filters.location !== "all") params.location = filters.location;
      if (filters.urgency !== "all") params.urgency = filters.urgency;
      const res = await api.get("/requests", { params });
      setData(res.data);
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [user, filters]);

  useEffect(() => { load(); }, [load]);

  const sectorLabel = (id) => {
    const s = meta.sectors.find((x) => x.id === id);
    return s ? (lang === "fr" ? s.fr : s.en) : id;
  };

  const onUnlock = async (req) => {
    try {
      const res = await api.post(`/requests/${req.id}/unlock`);
      setData((prev) => ({
        ...prev,
        requests: prev.requests.map((r) => r.id === req.id ? { ...r, ...res.data, unlocked: true } : r),
      }));
      toast.success(t("unlocked"));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
      if ([402, 403].includes(err.response?.status)) navigate("/pricing");
    }
  };

  if (!user) {
    return (
      <div className="min-h-[calc(100vh-4rem)] grid place-items-center px-4 text-center">
        <div className="max-w-md">
          <div className="grid place-items-center h-16 w-16 rounded-2xl bg-primary/10 text-primary mx-auto mb-6">
            <Inbox className="h-8 w-8" />
          </div>
          <h1 className="font-display text-3xl font-extrabold mb-3">{t("feed_title")}</h1>
          <p className="text-muted-foreground mb-8">{lang === "fr" ? "Connectez-vous pour voir les demandes de services en direct." : "Log in to see live service requests."}</p>
          <div className="flex justify-center gap-3">
            <Button data-testid="feed-login-btn" onClick={() => navigate("/login")}>{t("login")}</Button>
            <Button variant="outline" data-testid="feed-register-btn" onClick={() => navigate("/register")}>{t("register")}</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">{t("feed_title")}</h1>
          <p className="text-muted-foreground mt-1">{t("feed_sub")}</p>
        </div>
        {!data.subscribed && (
          <Button data-testid="feed-subscribe-btn" onClick={() => navigate("/pricing")}>{t("view_pricing")}</Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-8 p-4 rounded-2xl border border-border bg-card">
        <SlidersHorizontal className="h-4 w-4 text-muted-foreground" />
        <Select value={filters.sector} onValueChange={(v) => setFilters({ ...filters, sector: v })}>
          <SelectTrigger className="w-[180px]" data-testid="sector-filter-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("filter_sector")}</SelectItem>
            {meta.sectors.map((s) => <SelectItem key={s.id} value={s.id}>{lang === "fr" ? s.fr : s.en}</SelectItem>)}
          </SelectContent>
        </Select>
        <LocationSelect groups={meta.location_groups || []} value={filters.location}
          onChange={(v) => setFilters({ ...filters, location: v })} allLabel={t("filter_location")}
          testId="location-filter-select" className="w-[200px]" />
        <Select value={filters.urgency} onValueChange={(v) => setFilters({ ...filters, urgency: v })}>
          <SelectTrigger className="w-[160px]" data-testid="urgency-filter-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("filter_urgency")}</SelectItem>
            <SelectItem value="normal">{t("urgency_normal")}</SelectItem>
            <SelectItem value="urgent">{t("urgency_urgent")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-64 rounded-2xl" />)}
        </div>
      ) : data.requests.length === 0 ? (
        <div className="text-center py-24 text-muted-foreground" data-testid="feed-empty">
          <Inbox className="h-10 w-10 mx-auto mb-4 opacity-50" />
          {lang === "fr" ? "Aucune demande pour ces filtres." : "No requests for these filters."}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="feed-grid">
          {data.requests.map((req) => (
            <RequestCard key={req.id} req={req} sectorLabel={sectorLabel(req.sector)}
              onUnlock={onUnlock} subscribed={data.subscribed} />
          ))}
        </div>
      )}
    </div>
  );
}
